<script setup>
import { ref, computed } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const isExpanded = ref(false);

const formatDate = dateString => {
  return format(new Date(dateString), 'MMM d, yyyy');
};

const formatCurrency = (amount, currency) => {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(amount);
};

const statusLabel = computed(() => {
  const status = props.order.status?.toUpperCase().replace('-', '_');
  const key = `CONVERSATION_SIDEBAR.WOOCOMMERCE.ORDER_STATUS.${status}`;
  const translated = t(key);
  return translated === key ? props.order.status : translated;
});

const getStatusClass = status => {
  const classes = {
    completed: 'bg-n-teal-5 text-n-teal-12',
    processing: 'bg-n-blue-5 text-n-blue-12',
    pending: 'bg-n-amber-5 text-n-amber-12',
    cancelled: 'bg-n-solid-3 text-n-slate-12',
    refunded: 'bg-n-solid-3 text-n-slate-12',
    failed: 'bg-n-ruby-5 text-n-ruby-12',
  };
  return classes[status] || 'bg-n-solid-3 text-n-slate-12';
};

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};
</script>

<template>
  <div
    class="py-3 border-b border-n-weak last:border-b-0 flex flex-col gap-1.5"
  >
    <div class="flex justify-between items-center gap-2">
      <div class="font-medium flex min-w-0">
        <a
          :href="order.admin_url"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:underline text-n-slate-12 cursor-pointer truncate"
        >
          {{
            $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.ORDER_ID', {
              id: order.number || order.id,
            })
          }}
          <i class="i-lucide-external-link pl-1" />
        </a>
      </div>
      <div
        :class="getStatusClass(order.status)"
        class="text-xs px-2 py-1 rounded capitalize truncate shrink-0"
        :title="statusLabel"
      >
        {{ statusLabel }}
      </div>
    </div>
    <div class="text-sm text-n-slate-12">
      <span class="text-n-slate-11 border-r border-n-weak pr-2">
        {{ formatDate(order.date_created) }}
      </span>
      <span class="text-n-slate-11 pl-2">
        {{ formatCurrency(order.total, order.currency) }}
      </span>
    </div>
    <button
      v-if="order.line_items?.length"
      type="button"
      class="text-left text-sm text-n-brand flex items-center gap-1"
      @click="toggleExpanded"
    >
      <i
        :class="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
      />
      {{ $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.TOGGLE_ITEMS') }}
    </button>
    <ul v-if="isExpanded" class="flex flex-col gap-1 pl-2">
      <li
        v-for="(item, index) in order.line_items"
        :key="`${order.id}-${index}`"
        class="text-sm text-n-slate-11"
      >
        {{ item.quantity }} × {{ item.name }} —
        {{ formatCurrency(item.total, order.currency) }}
      </li>
    </ul>
  </div>
</template>
